// All component types live here. Components are plain data (no methods).
// Stores are accessed through World.store<ComponentType>(key).

import type { Genome } from './genome';
import type { StatKey } from './traits/base';
import type { EmergentEffect } from './traits/emergent';

export type Faction = 'ally' | 'enemy';

export interface Position {
  x: number;
  y: number;
}

export interface Velocity {
  vx: number;
  vy: number;
}

export interface Radius {
  r: number;
}

export interface Health {
  current: number;
  max: number;
}

export interface Age {
  seconds: number;
  lifespan: number;
}

/** Attached to allies only. */
export interface Fertility {
  /** When age/lifespan crosses into [start, end] the ball is fertile. */
  start: number; // fraction
  end: number; // fraction
}

export interface AttackComp {
  range: number;
  rate: number; // attacks per second
  damage: number;
  cooldown: number; // seconds until next allowed
  projectileSpeed: number;
}

export interface FactionComp {
  side: Faction;
}

export interface GenomeComp {
  genome: Genome;
}

export interface Stats {
  sheet: Record<StatKey, number>;
}

export interface EmergentComp {
  effect: EmergentEffect;
  ids: string[];
}

export interface RenderInfo {
  /** Packed 0xRRGGBB for fill. */
  color: number;
  /** Outline color. */
  outline: number;
}

export interface Projectile {
  /** Owner entity, used for friendly-fire avoidance. */
  ownerId: number;
  /** Owner faction. */
  side: Faction;
  damage: number;
  /** Pierces remaining: 0 = vanishes on first hit. */
  pierce: number;
  /** Lifetime in seconds before despawn. */
  ttl: number;
  /** Entities this projectile already damaged (so it doesn't double-hit on pierce). */
  hits: Set<number>;
  /** Frostbitten effect on hit (rolled per-target). */
  freeze?: { chance: number; duration: number };
}

/** Optional: marker that this entity has been collected (pending despawn). */
export interface Dead {
  reason: 'killed' | 'oldAge' | 'collected';
}

/** Marks a free-floating breeding pickup. Collection triggers the breed UI. */
export interface PickupComp {
  kind: 'breed';
  /** Lifetime in seconds before the pickup despawns if not collected. */
  ttl: number;
}

export type EnemyKind = 'seeker' | 'shooter' | 'bomber' | 'swarmer' | 'elite';

export interface EnemyKindComp {
  kind: EnemyKind;
}

/** Bomber state: fuse counts down once a target is in trigger range. */
export interface BomberComp {
  /** Seconds remaining on the fuse. Infinity until armed. */
  fuse: number;
  /** AOE radius and damage applied on detonation. */
  blastRadius: number;
  blastDamage: number;
}

/** Movement slow applied to enemies (from frostbitten projectile hits, etc).
 *  Decremented each tick by lifecycle. While remaining > 0, enemy movement
 *  speed is multiplied by `factor`. */
export interface SlowComp {
  remaining: number;
  factor: number; // 0..1
}

/** One-shot resurrection. Spawned on allies whose emergent set includes
 *  Phoenix. When health hits 0 and used=false, the lifecycle revives them
 *  and sets used=true. */
export interface PhoenixComp {
  used: boolean;
}

/** Periodic AOE damage from Pulsar emergent. */
export interface PulseTimerComp {
  remaining: number;
  intervalSec: number;
  radius: number;
  damage: number;
}

/** XP and level progression for ally balls. */
export interface XpComp {
  xp: number;
  level: number;
}

// Convenience: a registry of well-known store keys to avoid typo-divergence.
export const C = {
  position: 'position',
  velocity: 'velocity',
  radius: 'radius',
  health: 'health',
  age: 'age',
  fertility: 'fertility',
  attack: 'attack',
  faction: 'faction',
  genome: 'genome',
  stats: 'stats',
  emergent: 'emergent',
  render: 'render',
  projectile: 'projectile',
  dead: 'dead',
  pickup: 'pickup',
  enemyKind: 'enemyKind',
  bomber: 'bomber',
  slow: 'slow',
  phoenix: 'phoenix',
  pulseTimer: 'pulseTimer',
  xp: 'xp',
} as const;
